import React, { useRef, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    IconButton,
    Divider,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Stack,
    FormGroup,
    FormControlLabel,
    Checkbox,
    GlobalStyles,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import { useReactToPrint } from "react-to-print";
import { usePrefeitura } from "../../../../../Hooks/usePrefeitura";

interface Props {
    open: boolean;
    onClose: () => void;
    dados: any[];
    filtros: {
        ano: number;
        texto: string;
        situacao: string;
    };
}

export const ImprimirDialogImoveisRurais: React.FC<Props> = ({
    open,
    onClose,
    dados,
    filtros,
}) => {

    const printRef = useRef<HTMLDivElement>(null);

    const { prefeitura, logoPrefeitura } = usePrefeitura();

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
    });

    const [colunasVisiveis, setColunasVisiveis] = useState({
        numero: true,
        cartorio: true,
        localizacao: true,
        transmitente: true,
        adquirente: true,
        criado: true,
        enviado: true,
        avaliado: true,
    });

    type ColunaKey =
        | "numero"
        | "cartorio"
        | "localizacao"
        | "transmitente"
        | "adquirente"
        | "criado"
        | "enviado"
        | "avaliado";

    const toggleColuna = (coluna: ColunaKey) => {
        setColunasVisiveis((prev) => ({
            ...prev,
            [coluna]: !prev[coluna],
        }));
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">

            <GlobalStyles
                styles={{
                    "@media print": {
                        ".print-scale": {
                            zoom: 0.85,
                        },
                        "@page": {
                            size: "A4 landscape",
                            margin: "10mm",
                        },
                        ".no-print": { display: "none !important" },
                    },
                }}
            />

            <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
                <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    Imprimir Relatório de Imóveis Rurais
                </Typography>

                <IconButton onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>

                <Box className="no-print" mb={2}>

                    <Typography variant="subtitle1" fontWeight={600}>
                        Colunas a imprimir
                    </Typography>

                    <FormGroup row>

                        {(Object.keys(colunasVisiveis) as ColunaKey[]).map((key) => (
                            <FormControlLabel
                                key={key}
                                control={
                                    <Checkbox
                                        checked={colunasVisiveis[key]}
                                        onChange={() => toggleColuna(key)}
                                    />
                                }
                                label={key
                                    .replace("numero", "Código")
                                    .replace("cartorio", "Cartório")
                                    .replace("localizacao", "Localização")
                                    .replace("transmitente", "Transmitente")
                                    .replace("adquirente", "Adquirente")
                                    .replace("criado", "Criado")
                                    .replace("enviado", "Enviado")
                                    .replace("avaliado", "Avaliado")}
                            />
                        ))}

                    </FormGroup>

                    <Divider sx={{ mt: 2 }} />

                </Box>

                <div ref={printRef} className="print-scale" style={{ padding: 16 }}>

                    {/* Cabeçalho */}

                    <Box display="flex" justifyContent="center" alignItems="center">

                        {logoPrefeitura && (
                            <Box
                                component="img"
                                src={"https://solucoes.sinsoft.com.br/api/enderecos//uploads/" + logoPrefeitura}
                                sx={{ maxHeight: 70, mr: 4 }}
                            />
                        )}

                        <Box textAlign="center">
                            {prefeitura.NOME ? <> <Typography variant="h6" fontWeight="bold">
                                Prefeitura Municipal de {prefeitura.NOME}
                            </Typography>

                                <Typography variant="subtitle2">
                                    Estado do Rio Grande do Sul
                                </Typography></> : <></>}


                            <Typography
                                variant="subtitle2"
                                fontWeight="bold"
                                color="primary"
                            >
                                Relatório de Guias de ITBI Rurais
                            </Typography>

                        </Box>

                    </Box>

                    <Divider sx={{ borderBottomWidth: 2, my: 2 }} />

                    {/* filtros */}

                    <Stack direction="row" justifyContent="space-between" mb={2}>

                        <Typography variant="body2">
                            <b>Ano:</b> {filtros.ano}
                        </Typography>

                        <Typography variant="body2">
                            <b>Situação:</b> {filtros.situacao}
                        </Typography>

                        {filtros.texto && (
                            <Typography variant="body2">
                                <b>Filtro:</b> {filtros.texto}
                            </Typography>
                        )}

                    </Stack>

                    {/* tabela */}

                    <Table size="small">

                        <TableHead>
                            <TableRow>

                                {colunasVisiveis.numero && <TableCell>Código</TableCell>}
                                {colunasVisiveis.cartorio && <TableCell>Cartório</TableCell>}
                                {colunasVisiveis.localizacao && <TableCell>Localização</TableCell>}
                                {colunasVisiveis.transmitente && <TableCell>Transmitente</TableCell>}
                                {colunasVisiveis.adquirente && <TableCell>Adquirente</TableCell>}
                                {colunasVisiveis.criado && <TableCell>Criado</TableCell>}
                                {colunasVisiveis.enviado && <TableCell>Enviado</TableCell>}
                                {colunasVisiveis.avaliado && <TableCell>Avaliado</TableCell>}

                            </TableRow>
                        </TableHead>

                        <TableBody>

                            {dados.map((imovel, index) => (

                                <TableRow key={index}>

                                    {colunasVisiveis.numero && (
                                        <TableCell>
                                            {imovel.NUMERO}/{new Date(imovel.DATA_CRIACAO).getFullYear()}
                                        </TableCell>
                                    )}

                                    {colunasVisiveis.cartorio && (
                                        <TableCell>{imovel.CARTORIO_TITULO}</TableCell>
                                    )}

                                    {colunasVisiveis.localizacao && (
                                        <TableCell>{imovel.LOCALIZACAO}</TableCell>
                                    )}

                                    {colunasVisiveis.transmitente && (
                                        <TableCell>{imovel.TRANSMITENTE}</TableCell>
                                    )}

                                    {colunasVisiveis.adquirente && (
                                        <TableCell>{imovel.ADQUIRINTE}</TableCell>
                                    )}

                                    {colunasVisiveis.criado && (
                                        <TableCell>
                                            {imovel.DATA_CRIACAO
                                                ? new Date(imovel.DATA_CRIACAO).toLocaleDateString("pt-BR")
                                                : ""}
                                        </TableCell>
                                    )}

                                    {colunasVisiveis.enviado && (
                                        <TableCell>
                                            {imovel.ENVIADO
                                                ? new Date(imovel.ENVIADO).toLocaleDateString("pt-BR")
                                                : ""}
                                        </TableCell>
                                    )}

                                    {colunasVisiveis.avaliado && (
                                        <TableCell>
                                            {imovel.AVALIADO
                                                ? new Date(imovel.AVALIADO).toLocaleDateString("pt-BR")
                                                : ""}
                                        </TableCell>
                                    )}

                                </TableRow>

                            ))}

                        </TableBody>

                    </Table>

                    <Stack mt={2}>
                        <Typography variant="subtitle2">
                            Total de imóveis: <b>{dados.length}</b>
                        </Typography>
                    </Stack>

                </div>
            </DialogContent>

            <DialogActions>

                <Button onClick={onClose} color="error">
                    Cancelar
                </Button>

                <Button variant="contained" onClick={handlePrint}>
                    Imprimir
                </Button>

            </DialogActions>

        </Dialog>
    );
};